import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  CalendarDays,
  FileCheck2,
  FileText,
  Loader2,
  MessageSquareText,
  Phone,
  ShieldCheck,
  Users,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-white rounded-xl border border-[#D8E6F1] px-5 py-4 text-[#0C2A40] placeholder:text-[#93A5B5] focus:border-[#2880B8] focus:ring-2 focus:ring-[#2880B8]/20 focus:outline-none transition-all duration-300 text-base";

const platformModules = [
  { icon: FileText, label: "EHR + AI Scribe", value: "Clinical notes" },
  { icon: CalendarDays, label: "PMS", value: "Scheduling" },
  { icon: FileCheck2, label: "RCM", value: "Claims + AR" },
  { icon: Users, label: "HRMS", value: "Team ops" },
  { icon: MessageSquareText, label: "Secure Chat", value: "Care messaging" },
  { icon: Phone, label: "Calls", value: "Patient access" },
  { icon: Video, label: "Telehealth", value: "Virtual visits" },
  { icon: BarChart3, label: "Analytics", value: "Live reporting" },
];

function ModuleNode({ item, index, align = "left" }) {
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 + index * 0.05, duration: 0.42, ease: "easeOut" }}
      className={`relative h-12 overflow-hidden rounded-xl border border-[#9FD0EC]/22 bg-[#DDF3FF]/[0.075] px-3 py-2 shadow-[0_20px_55px_-42px_rgba(159,208,236,0.78)] backdrop-blur-xl ${
        align === "right" ? "text-right" : ""
      }`}
    >
      <motion.div
        animate={{ x: align === "right" ? ["140%", "-90%"] : ["-90%", "140%"] }}
        transition={{ repeat: Infinity, duration: 6.2 + index * 0.25, ease: "easeInOut" }}
        className="absolute inset-y-0 w-24 rotate-12 bg-gradient-to-r from-transparent via-[#DDF3FF]/12 to-transparent"
      />
      <div className={`relative flex items-center gap-3 ${align === "right" ? "flex-row-reverse" : ""}`}>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#9FD0EC]/14 text-[#9FD0EC]">
          <Icon size={14} />
        </span>
        <span className="min-w-0">
          <span className="block truncate font-mono text-[8px] tracking-[0.12em] uppercase text-[#9FD0EC]">{item.label}</span>
          <span className="mt-0.5 block truncate text-[13px] font-semibold leading-tight text-white">{item.value}</span>
        </span>
      </div>
    </motion.div>
  );
}

function ConnectorLane() {
  return <div className="h-12" aria-hidden="true" />;
}

function PlatformMapVisual() {
  const leftModules = platformModules.slice(0, 4);
  const rightModules = platformModules.slice(4);
  const rowCenters = [39, 99, 159, 219];
  const connectorPaths = [
    ...rowCenters.map((y) => `M168 ${y} H214 C240 ${y} 232 130 254 130`),
    ...rowCenters.map((y) => `M452 ${y} H406 C380 ${y} 388 130 366 130`),
  ];

  return (
    <div className="relative mx-auto mt-5 aspect-[620/260] w-full max-w-[620px] overflow-visible">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(159,208,236,0.14),transparent_28%),radial-gradient(circle_at_78%_70%,rgba(40,128,184,0.2),transparent_34%)]" />
      <div className="absolute left-1/2 top-[17.3%] aspect-square w-[27.4%] -translate-x-1/2 rounded-full border border-[#9FD0EC]/8" />
      <div className="absolute left-1/2 top-[25%] aspect-square w-[21%] -translate-x-1/2 rounded-full border border-dashed border-[#9FD0EC]/12" />
      <svg className="absolute inset-0 z-[1] h-full w-full overflow-visible" viewBox="0 0 620 260" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="loginConnectorGlow" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#DDF3FF" stopOpacity="0" />
            <stop offset="45%" stopColor="#DDF3FF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#9FD0EC" stopOpacity="0" />
          </linearGradient>
        </defs>
        {connectorPaths.map((path, index) => (
          <g key={path}>
            <path d={path} fill="none" stroke="#9FD0EC" strokeOpacity="0.2" strokeWidth="1" />
            <motion.path
              d={path}
              fill="none"
              stroke="url(#loginConnectorGlow)"
              strokeLinecap="round"
              strokeWidth="2"
              strokeDasharray="48 220"
              animate={{ strokeDashoffset: [260, 0], opacity: [0, 1, 1, 0] }}
              transition={{ repeat: Infinity, duration: 3 + index * 0.08, ease: "linear", times: [0, 0.12, 0.88, 1] }}
            />
          </g>
        ))}
      </svg>

      <div className="relative z-10 grid h-full grid-cols-[168px_64px_156px_64px_168px] items-center">
        <div className="grid gap-3">
          {leftModules.map((item, index) => (
            <ModuleNode key={item.label} item={item} index={index} />
          ))}
        </div>

        <div className="grid gap-3">
          {leftModules.map((item, index) => (
            <ConnectorLane key={item.label} />
          ))}
        </div>

      <motion.div
        animate={{ y: [0, -6, 0], scale: [1, 1.012, 1] }}
        transition={{ repeat: Infinity, duration: 5.8, ease: "easeInOut" }}
          className="relative z-20 mx-auto flex h-28 w-28 items-center justify-center"
      >
        <div className="absolute inset-0 rounded-[26px] border border-[#9FD0EC]/18 bg-[#DDF3FF]/7 shadow-[0_0_70px_rgba(159,208,236,0.24)]" />
        <div className="absolute inset-3 rounded-[21px] border border-[#9FD0EC]/18 bg-[#071F33]/68 backdrop-blur-2xl" />
        <motion.div
          animate={{ opacity: [0.25, 0.65, 0.25], rotate: [0, 180, 360] }}
          transition={{ repeat: Infinity, duration: 16, ease: "linear" }}
          className="absolute inset-5 rounded-[18px] border border-dashed border-[#9FD0EC]/28"
        />
        <div className="relative flex flex-col items-center justify-center text-center">
          <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white p-1.5 shadow-[0_0_52px_rgba(221,243,255,0.55)]">
            <img src="/astra-logo.png" alt="Astra" className="h-full w-full object-contain" />
          </div>
          <p className="mt-3 font-mono text-[9px] tracking-[0.16em] uppercase text-[#DDF3FF]">Astra AIOS</p>
        </div>
      </motion.div>

        <div className="grid gap-3">
          {rightModules.map((item, index) => (
            <ConnectorLane key={item.label} />
          ))}
        </div>

        <div className="grid gap-3">
          {rightModules.map((item, index) => (
            <ModuleNode key={item.label} item={item} index={index + 4} align="right" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(form.email, form.password);
      toast.success("Welcome back to Astra.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="login-page">
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_80%_20%,rgba(40,128,184,0.06),transparent_28%)]" />

      <div className="relative hidden lg:flex h-screen overflow-y-auto overflow-x-hidden bg-[#071F33] px-6 py-4 xl:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(159,208,236,0.18),transparent_26%),radial-gradient(circle_at_70%_58%,rgba(40,128,184,0.28),transparent_34%),linear-gradient(135deg,#071F33_0%,#0B2D46_58%,#155C86_100%)]" />
        <div className="absolute inset-0 opacity-20 dot-grid" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#9FD0EC]/45 to-transparent" />

        <div className="relative z-10 flex min-h-full w-full flex-col" style={{ zoom: 1.1 }}>
          <Link to="/" className="w-fit shrink-0">
            <div className="bg-white rounded-xl px-3.5 py-2.5 inline-flex shadow-lg">
              <img src="/astra-logo.png" alt="Astra" className="h-8 w-auto" />
            </div>
          </Link>

          <div className="min-h-0 flex-1 flex flex-col justify-start pt-4">
            <div className="max-w-[760px]">
              <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#9FD0EC]">Astra workspace access</p>
              <h2 className="mt-3 max-w-[620px] font-display text-[clamp(2.15rem,3vw,3.25rem)] font-bold leading-[0.98] tracking-tight text-white">
                One connected platform for clinical, revenue and team operations.
              </h2>
              <p className="mt-3 max-w-[620px] text-[14px] leading-6 text-[#DDF3FF]/80">
                EHR + AI Scribe, PMS, RCM, HRMS, secure chat, calls and telehealth connected inside one Astra workspace.
              </p>
            </div>

            <PlatformMapVisual />
          </div>

          <div className="shrink-0 pb-1 pt-4">
            <p className="font-display text-[clamp(1.2rem,1.45vw,1.625rem)] font-medium tracking-tighter leading-[1.06] text-white">
              Everything your practice runs on - <span className="font-extrabold text-[#9FD0EC]">now intelligent.</span>
            </p>
            <p className="mt-2 flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] uppercase text-[#9FD0EC]">
              <ShieldCheck size={15} /> HIPAA-ready workspace access
            </p>
          </div>
        </div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center p-6 md:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-lg" style={{ zoom: 1.1 }}>
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.16em] uppercase text-[#51667A] hover:text-[#0C2A40] transition-colors mb-14">
            <ArrowLeft size={15} /> Back
          </Link>
          <h1 className="font-display text-6xl font-bold tracking-tighter text-[#0C2A40]">Log in</h1>
          <p className="mt-5 text-xl text-[#51667A]">Access your Astra workspace.</p>

          <form onSubmit={submit} className="mt-11 space-y-4">
            <input data-testid="login-email" required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input data-testid="login-password" required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            {error && <p data-testid="login-error" className="text-sm text-red-500 font-mono">{error}</p>}
            <button data-testid="login-submit" type="submit" disabled={loading} className="w-full min-h-[58px] rounded-full bg-[#2880B8] py-4 text-white font-mono text-xs tracking-[0.16em] uppercase hover:bg-[#1F6EA1] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : (<>Log In <ArrowRight size={17} /></>)}
            </button>
          </form>

          <p className="mt-9 text-base text-[#51667A]">
            No account?{" "}
            <a href="/#book" data-testid="link-signup" className="text-[#2880B8] font-medium hover-underline">Get a demo</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
